import React, { useEffect, useState, useRef, useCallback } from 'react';
import '../TextPrompt.css';
import { useTranslation } from 'react-i18next';
import axios, { AxiosError } from 'axios';
import ButtonSidebar2 from '../ButtonSidebar/ButtonSidebar2';
import { useHistory, Prompt } from 'react-router-dom'; // Importamos Prompt para la advertencia
import arrowSVG from '../Iconos/arrow.svg';
import checkSVG from '../Iconos/check.svg';
import edit2SVG from '../Iconos/edit-2.svg';
import TextToneSelector from './TextToneSelector';

const VALID_MINUTES = 15;

function ScriptAvatarPrompt() {
  const history = useHistory();
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [selectedTone, setSelectedTone] = useState('neutral');
  const [selectedVoice, setSelectedVoice] = useState('es-ES-AlvaroNeural');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessStarted, setIsProcessStarted] = useState(false); // Estado para controlar si el proceso ha comenzado

  const editableRef = useRef<HTMLDivElement>(null);

  const generateSessionId = () => {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  useEffect(() => {
    const existingSessionId = localStorage.getItem('sessionId');
    if (!existingSessionId) {
      const newSessionId = generateSessionId();
      localStorage.setItem('sessionId', newSessionId);
    }
  }, []);

  const handleToneChange = async (newTone: string) => {
    setSelectedTone(newTone);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/change-tone`, {
        text: generatedText,
        tone: newTone,
      });
      setGeneratedText(response.data);
    } catch (error) {
      console.error("Error changing text tone:", error);
    }
  };
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isProcessStarted) {
        event.preventDefault();
        event.returnValue = ''; // Mostrar advertencia
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isProcessStarted]);
  useEffect(() => {
    console.log("El valor de isProcessStarted ha cambiado:", isProcessStarted);
  }, [isProcessStarted]);
  const handleVoiceChange = (voice: string) => {
    setSelectedVoice(voice);
    console.log('Voice selected:', voice);
  };

  const handleSaveDraft = async () => {
    if (!generatedText.trim()) {
      alert('El campo de texto no puede estar vacío.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('No estás autenticado.');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/save-text-draft`, {
        contenido_texto: generatedText
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert('Borrador de texto guardado con éxito');
    } catch (error) {
      console.error('Error al guardar el borrador de texto:', error);
      alert('Error al guardar el borrador de texto');
    }
  };

  const saveTextToLocalStorage = useCallback(() => {
    const textData = {
      timestamp: new Date().getTime(),
      content: generatedText,
    };
    localStorage.setItem('ScriptData', JSON.stringify(textData));
  }, [generatedText]);

  const placeholder = t('Placeholder');

  const handleTextChange = (e: React.FormEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    const range = sel?.getRangeAt(0);
    const startOffset = range?.startOffset;

    setGeneratedText(e.currentTarget.textContent || '');

    setTimeout(() => {
      if (range && editableRef.current && editableRef.current.childNodes[0]) {
        const newRange = document.createRange();
        const selection = window.getSelection();
        newRange.setStart(editableRef.current.childNodes[0], startOffset ?? 0);
        newRange.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(newRange);
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessStarted(true); // Marcar que el proceso ha comenzado
    setIsLoading(true); // Mostrar indicador de carga

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/generate-script`, {
        text: prompt,
        voice: selectedVoice,
      });
      const respuesta = response.data.content;
      setGeneratedText(respuesta);
    } catch (error) {
      console.error("Error generating description:", error);
    } finally {
      setIsLoading(false); // Detener indicador de carga
    }
  };

  const handlePreviousClick = () => {
    history.push('/Social-Networking-Avatar');
  };

  const handleNextClick = async () => {
    if (!selectedImage && !generatedText.trim()) {
      alert('Faltan la imagen y el texto.');
      return;
    }
    if (!selectedImage) {
      alert('Falta la imagen.');
      return;
    }
    if (!generatedText.trim()) {
      alert('Falta el texto.');
      return;
    }
    setIsLoading(true);

    try {
      const sessionId = localStorage.getItem('sessionId');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

      const response = await axios.post(`${backendUrl}/generate-video`, {
        imageUrl: selectedImage,
        text: generatedText,
        voice: selectedVoice,
        sessionId,
      });

      const videoData = {
        sessionId: sessionId,
        timestamp: new Date().getTime()
      };
      localStorage.setItem('videoData', JSON.stringify(videoData));
      history.push('/Social-Networking-Avatar3');
    } catch (error) {
      console.error('Error generating video:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const ScriptDataJSON = localStorage.getItem('ScriptData');
    if (ScriptDataJSON) {
      const textData = JSON.parse(ScriptDataJSON);
      const timeNow = new Date().getTime();
      const timeElapsed = (timeNow - textData.timestamp) / 1000 / 60;

      if (timeElapsed < VALID_MINUTES) {
        setGeneratedText(textData.content);
      } else {
        localStorage.removeItem('ScriptData');
      }
    }

    const imageDataJSON = localStorage.getItem('avatarData');
    if (imageDataJSON) {
      const imageData = JSON.parse(imageDataJSON);
      const timeNow = new Date().getTime();
      const timeElapsed = (timeNow - imageData.timestamp) / 1000 / 60;

      if (timeElapsed < VALID_MINUTES) {
        setSelectedImage(imageData.imageUrl);
      } else {
        localStorage.removeItem('avatarData');
      }
    }
  }, []);

  useEffect(() => {
    if (generatedText) {
      saveTextToLocalStorage();
    }
  }, [generatedText, saveTextToLocalStorage]);

  function handleOptimizeText(): void {
    throw new Error('Function not implemented.');
  }

  return (
    <div className='container-text'>
      {/* Advertencia al intentar salir durante el proceso */}
      <Prompt
        when={isProcessStarted}
        message="El proceso ha comenzado, ¿estás seguro de que deseas salir? Se perderán los cambios no guardados."
      />

      <div className='text-container'>
        <TextToneSelector
          onToneChange={handleToneChange}
          selectedTone={selectedTone}
          onVoiceChange={handleVoiceChange}
        />
        <div className="text-prompt-container">
          <div className="image-prompt-title">
            <p>{t('Escribe sobre qué quieres hablar')} <span className="highlight">{t('TextPrompt.ImageHere')}</span>.</p>
          </div>
          <div className="text-prompt-description">
            <form onSubmit={handleSubmit}>
              <input
                className='text-input-prompt'
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={placeholder}
              />
              <button className="select-button2" type="submit">
                <span className="image-prompt-button-icon"></span>
                <div className='text-prompt-image'>
                  <div><img src={edit2SVG} alt="" /></div>
                  <div>{t('TextPrompt.create')}</div>
                </div>
              </button>
            </form>
          </div>
          <div className="generated-text-container">
            <div
              ref={editableRef}
              className='generated-text'
              contentEditable
              onInput={handleTextChange}
              suppressContentEditableWarning={true}
            >
              {generatedText}
            </div>
          </div>

          <div className="image-selection-navigation">
            <button className="image-selection-previous" onClick={handlePreviousClick}>
              <img src={arrowSVG} alt="" className='reverse-arrow' /> {t('Anterior')}
            </button>
            <button className="image-selection-next" onClick={handleNextClick}>
              {t('Siguiente')} <img src={arrowSVG} alt="" className='normal-arrow' />
            </button>
          </div>
          {isLoading && <div>
            <div className="loading-bar"></div>
            <p className="loading-text">Cargando...</p>
          </div>}

        </div>
      </div>
      <ButtonSidebar2
        onSaveDraft={handleSaveDraft}
        onEdit={function (): void {
          throw new Error('Function not implemented.');
        }}
        onOptimize={handleOptimizeText}
      />
    </div>
  );
}

export default ScriptAvatarPrompt;
